import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { RoomParticipant, ParticipantStatus } from "../../../entities/room-participant.entity";
import {
  FiMic,
  FiMicOff,
  FiVideo,
  FiVideoOff,
  FiStar,
  FiUser,
  FiClock,
  FiLogIn,
  FiLogOut,
  FiTrendingUp,
  FiTrendingDown,
} from "react-icons/fi";
import { User } from "src/features/users/types/user.entity";
import { Timestamp } from "firebase/firestore";

type ParticipantsSectionProps = {
  participants: RoomParticipant[];
  hostUserId: string;
  usersMap: Record<string, User>;
};

const statusColors: Record<ParticipantStatus, string> = {
  ON_STAGE: "bg-green-100 text-green-800 border-green-200",
  AUDIENCE: "bg-blue-100 text-blue-800 border-blue-200",
  REQUEST_STAGE: "bg-yellow-100 text-yellow-800 border-yellow-200",
  LEFT: "bg-gray-100 text-gray-800 border-gray-200",
  KICKED_OUT: "bg-red-100 text-red-800 border-red-200",
};

const formatTimestamp = (timestamp: Timestamp | null | undefined): string => {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const ParticipantsSection: React.FC<ParticipantsSectionProps> = ({
  participants,
  hostUserId,
  usersMap,
}) => {

  // Separate participants by status
  const onStage = useMemo(() => participants.filter((p) => p.status === "ON_STAGE"), [participants]);
  const audience = useMemo(() => participants.filter((p) => p.status === "AUDIENCE"), [participants]);
  const requestingStage = useMemo(() => participants.filter((p) => p.status === "REQUEST_STAGE"), [participants]);
  const left = useMemo(() => participants.filter((p) => p.status === "LEFT" || p.status === "KICKED_OUT"), [participants]);

  const ParticipantCard = ({ participant }: { participant: RoomParticipant }) => {
    const isHost = participant.userId === hostUserId;
    const user = usersMap[participant.userId];
    const isActive = !participant.leftAt;

    return (
      <Link
        to={`/users/${participant.userId}`}
        className={`flex items-center justify-between p-4 rounded-xl border no-underline ${
          statusColors[participant.status]
        } transition-all duration-200 hover:shadow-md hover:scale-[1.01] cursor-pointer block`}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            {user?.pictureURL ? (
              <img
                src={user.pictureURL}
                alt={user.name || "User"}
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <FiUser className="w-5 h-5 text-gray-500" />
              </div>
            )}
            {isHost && (
              <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1">
                <FiStar className="w-3 h-3 text-white" />
              </div>
            )}
            {isActive && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-900 text-sm">
                {user?.name || participant.userId}
              </p>
              {isHost && <span className="text-xs text-yellow-600 font-medium">(Host)</span>}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-1">
                <FiClock className="w-3 h-3" />
                {Math.round(participant.duration)} min
              </span>
              <span className="flex items-center gap-1">
                <FiLogIn className="w-3 h-3 text-green-600" />
                {formatTimestamp(participant.joinedAt)}
              </span>
              {participant.leftAt ? (
                <span className="flex items-center gap-1">
                  <FiLogOut className="w-3 h-3 text-red-500" />
                  {formatTimestamp(participant.leftAt)}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  Active
                </span>
              )}
              {participant.earnedMoney?.totalAmount != null && (
                <span className="flex items-center gap-1 text-green-600 font-medium">
                  <FiTrendingUp className="w-3 h-3" />
                  {Number(participant.earnedMoney.totalAmount).toFixed(2)} {participant.earnedMoney.currency || ''}
                </span>
              )}
              {participant.deductedMoney?.totalAmount != null && (
                <span className="flex items-center gap-1 text-red-600 font-medium">
                  <FiTrendingDown className="w-3 h-3" />
                  {Number(participant.deductedMoney.totalAmount).toFixed(2)} {participant.deductedMoney.currency || ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {participant.status === "ON_STAGE" && (
            <>
              {participant.isMuted ? (
                <FiMicOff className="w-4 h-4 text-red-500" />
              ) : (
                <FiMic className="w-4 h-4 text-green-500" />
              )}
              {participant.isVideoOn ? (
                <FiVideo className="w-4 h-4 text-green-500" />
              ) : (
                <FiVideoOff className="w-4 h-4 text-gray-400" />
              )}
            </>
          )}
          <span className="text-xs px-2 py-1 rounded-full bg-white bg-opacity-50">
            {participant.status.replace("_", " ")}
          </span>
        </div>
      </Link>
    );
  };

  const ParticipantGroup = ({
    title,
    items,
  }: {
    title: string;
    items: RoomParticipant[];
  }) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-6 last:mb-0">
        <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
          {title} ({items.length})
        </h4>
        <div className="space-y-2">
          {items.map((p) => (
            <ParticipantCard key={p.id} participant={p} />
          ))}
        </div>
      </div>
    );
  };

  if (participants.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FiUser className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No participants yet</p>
      </div>
    );
  }

  return (
    <div>
      <ParticipantGroup title="On Stage" items={onStage} />
      <ParticipantGroup title="Requesting Stage" items={requestingStage} />
      <ParticipantGroup title="Audience" items={audience} />
      <ParticipantGroup title="Left / Kicked Out" items={left} />
    </div>
  );
};
